import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  getQuizColorByIndex,
  getQuizPaddingDashboardQuizByIndex,
} from "../../utils/utility-functions";
import { useDispatch } from "react-redux";
import FunnelStepTypes from "./component";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import Guid from "guid";
import Slider from "react-slick";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { LOCALE } from "../../utils/constants";

const PrevArrow = (props) => {
  const { onClick } = props;
  return (
    <div
      className="arrow left-arrow absolute left-[-20px] sm:left-[-30px] md:left-[-40px] top-1/2 transform -translate-y-1/2 cursor-pointer z-10"
      onClick={onClick}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="50"
        height="95"
        viewBox="0 0 24 24"
        fill="none"
        stroke="grey"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="feather feather-chevron-left w-6 h-12 sm:w-10 sm:h-16 md:w-[100px] md:h-[190px]"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </div>
  );
};

const NextArrow = (props) => {
  const { onClick } = props;
  return (
    <div
      className="arrow right-arrow absolute right-[-20px] sm:right-[-30px] md:right-[-40px] top-1/2 transform -translate-y-1/2 cursor-pointer z-10"
      onClick={onClick}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="50"
        height="95"
        viewBox="0 0 24 24"
        fill="none"
        stroke="grey"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="feather feather-chevron-right w-6 h-12 sm:w-10 sm:h-16 md:w-[100px] md:h-[190px]"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </div>
  );
};

const PushAnswerCountToFunnelStep = (answerCount) => ({
  type: FunnelStepTypes.SET_ANSWER_COUNT,
  answerCount,
});
const PushSelectedAnswerCountToFunnelStep = (count) => ({
  type: FunnelStepTypes.SET_SELECTED_ANSWER_COUNT,
  count,
});
const PushAssessmentIdToFunnelStep = (assessmentId) => ({
  type: FunnelStepTypes.SET_ASSESSMENT_ID,
  assessmentId,
});
const PushAssessmentAttemptIdToFunnelStep = (assessmentAttemptId) => ({
  type: FunnelStepTypes.SET_ASSESSMENT_ATTEMPT_ID,
  assessmentAttemptId,
});
const pushResetSelectedAnswerCountToFunnelStep = () => ({
  type: FunnelStepTypes.RESET_SELECTED_ANSWER_COUNT,
});

const DashboardQuizB = ({
  getBulkQuestionsConnect,
  saveUserAnswerConnect,
  updateUserAnswerConnect,
  createAssessmentAttemptConnect,
  getAllAssessmentConnect,
  funnelStep,
  handleContinueTest,
  getOneQuestionPerAssessmentConnect,
  getAllAssessmentData,
  attemptAssessmentIds,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [savedUserAnswer, setSavedUserAnswer] = useState([]);
  const { userGUID } = funnelStep || {};
  const [quizData, setQuizData] = useState([]);
  const dispatch = useDispatch();
  const newAssessmentAttemptId = Guid.create().toString();
  const { t } = useTranslation();
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;

  const baseAssessmentIds = useMemo(
    () => [
      PERSONALITY_TEST_ASSESSMENT_ID,
      ENNEAGRAM_TEST_ASSESSMENT_ID,
      DISC_TEST_ASSESSMENT_ID,
      BIG_FIVE_TEST_ASSESSMENT_ID,
    ],
    [],
  );

  // Get IDs that exist in both baseAssessmentIds and attemptAssessmentIds
  const overlappingIds = useMemo(
    () => baseAssessmentIds.filter((id) => attemptAssessmentIds?.includes(id)),
    [baseAssessmentIds, attemptAssessmentIds],
  );

  // IDs from base that are not already attempted
  const filteredBaseIds = useMemo(
    () => baseAssessmentIds?.filter((id) => !overlappingIds?.includes(id)),
    [baseAssessmentIds, overlappingIds],
  );

  // Get all unique available assessments not in attemptAssessmentIds
  const availableAssessments = useMemo(
    () =>
      getAllAssessmentData
        .filter((item) => !attemptAssessmentIds?.includes(item?.id)) // Remove attempted ones
        .map((item) => item?.id), // Extract only IDs
    [getAllAssessmentData, attemptAssessmentIds],
  );

  // Function to shuffle an array
  const shuffleArray = (array) => [...array].sort(() => Math.random() - 0.5);

  // Pick random unique replacements
  const replacementIds = useMemo(() => {
    if (overlappingIds.length === 0) return [];

    // Shuffle available and pick as many as needed
    return shuffleArray(availableAssessments).slice(0, overlappingIds.length);
  }, [overlappingIds, availableAssessments]);

  // Final selection of 5 unique assessment IDs
  const assessmentIds = useMemo(() => {
    const uniqueSet = new Set([...filteredBaseIds, ...replacementIds]);

    // If we still need more to reach 5, add from available unique assessments
    const remainingNeeded = 5 - uniqueSet.size;
    if (remainingNeeded > 0) {
      shuffleArray(availableAssessments)
        .filter((id) => !uniqueSet.has(id)) // Ensure uniqueness
        .slice(0, remainingNeeded)
        .forEach((id) => uniqueSet.add(id));
    }

    return [...uniqueSet].slice(0, 5); // Ensure exactly 5 unique IDs
  }, [filteredBaseIds, replacementIds, availableAssessments]);

  useEffect(() => {
    getOneQuestionPerAssessmentConnect(assessmentIds, currentLocale).then(
      (res) => {
        if (res) {
          const questionsArray = Object.values(res);
          setQuizData(questionsArray);
        }
      },
    );
  }, [assessmentIds, currentLocale, getOneQuestionPerAssessmentConnect]);

  const handleAnswerChange = (e, questionId, questionIndex, assessmentId) => {
    if (isProcessing) return;

    setIsProcessing(true);
    const { value } = e.target;

    // Update the answers in local state
    setUserAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: Number(value),
    }));

    // Prepare user answer data for API
    let userAnswerData = {
      assessmentAttemptId: newAssessmentAttemptId,
      userId: userGUID,
      questionId: questionId,
      assessmentId: assessmentId,
      answer: value,
    };
    let userStageData = {
      userId: userGUID,
      asessmentId: assessmentId,
      assessmentAttemptId: newAssessmentAttemptId,
      assessmentStep: 1,
      resumeQuestionId: currentIndex + 1,
    };

    const isExistingAnswer = savedUserAnswer.some(
      (item) => item.questionId === questionId,
    );

    // Decide whether to save or update the answer
    const processAnswer = isExistingAnswer
      ? updateUserAnswerConnect(userAnswerData)
      : saveUserAnswerConnect(userAnswerData).then((res) => {
          if (res) {
            handleContinueTest(assessmentId, newAssessmentAttemptId, true);
          }
        });

    processAnswer
      .then(() => {
        // Update savedUserAnswer in state
        setSavedUserAnswer((prev) =>
          isExistingAnswer
            ? prev.map((item) =>
                item.questionId === questionId
                  ? { ...item, answer: Number(value) }
                  : item,
              )
            : [...prev, { questionId, answer: Number(value) }],
        );

        // Update funnel steps
        if (!isExistingAnswer) {
          dispatch(PushAnswerCountToFunnelStep(currentIndex + 1));
          dispatch(PushSelectedAnswerCountToFunnelStep(currentIndex + 1));
        }
        setIsProcessing(false);
      })
      .catch(() => {
        setIsProcessing(false); // Reset processing state on error
      });

    // Create or update assessment attempt
    if (!isExistingAnswer) {
      if (currentIndex + 1 === 1) {
        createAssessmentAttemptConnect(userStageData).then((response) => {});
      }
    }
  };

  const settings = {
    dots: false,
    infinite: true,
    autoplay: true,
    speed: 1500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  return (
    <div className="w-full">
      {quizData.length > 0 ? (
        <Slider
          {...settings}
          className="w-[90%] mx-auto "
          swipe={quizData.length > 1}
          draggable={quizData.length > 1}
          arrows={quizData.length > 1}
          infinite={quizData.length > 1}
        >
          {quizData.map((question, index) => (
            <QuestionContainer key={question.question.id}>
              <form
                className="w-full mx-auto py-1 px-0 sm:px-0"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  height: "100%",
                  justifyContent: "space-between",
                  maxWidth: "1010px",
                }}
              >
                <div
                  className="text-center mx-auto w-full px-1"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                  }}
                >
                  <h1 className="text-base sm:text-xl font-semibold text-black w-full leading-snug">
                    {t("quiz.start_your")} {question.title}
                  </h1>
                </div>

                <div
                  className="text-center w-full px-1"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                  }}
                >
                  <h5 className="sm:text-3xl font-semibold text-insightful-purple w-full leading-snug">
                    {question.question.title_EN}
                  </h5>
                </div>

                {/* Input Options */}

                <LabelContainer className="max-w-screen-sm sm:max-w-full flex flex-col sm:flex-row items-center justify-between w-full flex-1 md:gap-2">
                  <LabelText className="hidden sm:flex text-[#36A2B8] md:mr-1 lg:mr-4 absolute left-0 md:left-auto bottom-0 md:bottom-auto md:relative disagree-label">
                    {t("quiz.disagree")}
                  </LabelText>
                  <CircleContainer
                    className=" mx-auto mb-3 flex gap-1 sm:gap-2 md:gap-2 lg:gap-5 flex-shrink"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    {Array.from(Array(question.optionCount), (_, i) => (
                      <label key={i} className="text-center">
                        <input
                          type="radio"
                          name={`question-${question.question.id}`}
                          className="hidden"
                          onChange={(e) =>
                            handleAnswerChange(
                              e,
                              question.question.id,
                              index,
                              question?.assessmentId,
                            )
                          }
                          checked={userAnswers[question.question.id] === i + 1}
                          value={i + 1}
                          // disabled={index > currentIndex || isQuizComplete}
                        />
                        <div
                          className={`rounded-full p-0 flex mx-0 justify-center items-center border-2 cursor-pointer 
                      ${getQuizPaddingDashboardQuizByIndex(
                        i,
                        question?.optionCount,
                      )} 
                      bg-[#f5f5f5] hover:bg-insightful-light-purple transition-all duration-300`}
                          style={{
                            borderColor:
                              userAnswers[question?.question.id] === i + 1
                                ? "#805AED"
                                : getQuizColorByIndex(i, question?.optionCount)
                                    .color,
                            backgroundColor:
                              userAnswers[question.question.id] === i + 1
                                ? "#805AED"
                                : "",
                          }}
                        >
                          {userAnswers[question.question.id] === i + 1 && (
                            <span>
                              <svg
                                className="w-7 fill-white"
                                clip-rule="evenodd"
                                fillRule="evenodd"
                                stroke-linejoin="round"
                                stroke-miterlimit="2"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="m2.25 12.321 7.27 6.491c.143.127.321.19.499.19.206 0 .41-.084.559-.249l11.23-12.501c.129-.143.192-.321.192-.5 0-.419-.338-.75-.749-.75-.206 0-.411.084-.559.249l-10.731 11.945-6.711-5.994c-.144-.127-.322-.19-.5-.19-.417 0-.75.336-.75.749 0 .206.084.412.25.56"
                                  fillRule="nonzero"
                                />
                              </svg>
                            </span>
                          )}
                        </div>
                      </label>
                    ))}
                  </CircleContainer>
                  <LabelText className="text-[#53AF65] hidden sm:flex md:ml-1 lg:ml-4">
                    {t("quiz.agree")}
                  </LabelText>
                  {/* Bottom-aligned labels */}
                  <div className="flex justify-between px-2 mt-2 w-full sm:hidden">
                    <LabelText className="text-[#36A2B8] flex">
                      {t("quiz.disagree")}
                    </LabelText>
                    <LabelText className="text-[#53AF65] flex sm:hidden">
                      {t("quiz.agree")}
                    </LabelText>
                  </div>
                </LabelContainer>
              </form>
            </QuestionContainer>
          ))}
        </Slider>
      ) : (
        <div className="flex flex-col gap-4 items-center justify-center py-10 w-[80%] mx-auto">
          <div className="w-3/4 h-6 bg-gray-300 animate-pulse rounded" />
          <div className="w-2/3 h-8 bg-gray-300 animate-pulse rounded" />
          <div className="flex gap-3 mt-6">
            {Array.from({ length: 5 }).map((_, idx) => (
              <div
                key={idx}
                className="w-10 h-10 bg-gray-300 animate-pulse rounded-full"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardQuizB;

const LabelText = styled.div`
  font-size: 1rem;
  font-weight: bold;
  white-space: nowrap;

  /* iPad fix */
  &.disagree-label {
    @media (min-width: 768px) and (max-width: 820px) {
      position: relative !important;
      left: auto !important;
      bottom: auto !important;
      margin-right: 8px;
    }
  }

  /* to reduced margin on tablet only */
  @media (min-width: 768px) and (max-width: 1023px) {
    margin: 0 0.3rem;
    font-size: 0.9rem;
  }

  /* existing desktop/mobile */
  @media (min-width: 1024px) {
    margin: 0 1rem;
  }
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  items: center;
  flex-direction: column;
  padding: 0 15px;
`;

const LabelContainer = styled.div`
  display: flex;
  width: 100%;
`;

const QuestionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  min-height: 233px;
  overflow: hidden;
`;

const CircleContainer = styled.div`
  display: flex;
  align-items: center;

  /* to prevent tablet overflow */
  @media (min-width: 768px) and (max-width: 1023px) {
    min-width: 0;
  }
`;
